import { useEffect, useId, useState } from 'react';
import { ApiError } from '@orgazone/api-envelope/client';
import { dateTime } from '@orgazone/date-format';
import { confirm, Picker, Tabs } from '@orgazone/ui-web';
import { bytesToBase64 } from '../base64';
import { api, INBOUND_MSG_TYPES, msgTypeLabel, type ApiMapping, type ApiMappingDetail, type MappingReport, type MappingValidateResult, type StoredMappingReport } from '../api';
import { attachCandidates, attachConflict, flowCandidates, flowLabel, flowToLabel, inServiceLabel, readFlowLabel, type MappingFlow } from '../mapping-rules';
import { Icon } from './icons';
import { TypeTile, typeTint } from './message-type';
import { Modal } from './Modal';
import { Badge, Btn, Dot, Spinner } from './ui';
import { useApp } from '../state';

// LES FENÊTRES D'UN MAPPING PARTAGÉ — sa fiche, le rattachement à un fournisseur, la création et la nouvelle version.
// Elles servent à la bibliothèque (écran Mappings), à la fiche d'un fournisseur et à son assistant : un mapping se
// rattache depuis le fournisseur, ses versions se gèrent sur sa fiche, à un seul endroit pour tous ses fournisseurs.
// ⚠️ Ce qui s'affiche après une écriture est RELU au serveur, jamais supposé : un rattachement refusé (le fournisseur
//    reçoit déjà ce flux) l'est par la base, et l'écran montre son motif tel quel.

/** Le flux lu dans un fichier reçu : ce qu'un mapping doit reconnaître pour le traduire. */
export interface ReadFlow { format: string; discriminant: string }

/** Un fichier exemple : ses octets en base64, et son nom (un mapping `fileNameGln` y lit le magasin). */
export interface SampleFile { base64: string; name: string | null }

/** Un mapping tel qu'un fournisseur le porte : assez pour l'afficher et juger un conflit de flux — et, rattaché, « à revoir ». */
export type AttachedMapping = MappingFlow & { id: string; name: string; currentVersion: number; errorCount?: number };

const sectionSt = { fontSize: 'var(--t-sub)', fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--ink-500)' } as const;
const inp = { height: 38, borderRadius: 'var(--r-ctrl)', border: '1px solid var(--ink-200)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-lead)', padding: '0 12px', width: '100%' } as const;
const ta = { width: '100%', borderRadius: 'var(--r-sm)', border: '1px solid var(--ink-200)', background: 'var(--ink-50)', color: 'var(--ink-800)', fontSize: 'var(--t-note)', fontFamily: 'var(--font-mono)', padding: 10 } as const;
const linkBtn = { background: 'none', border: 'none', padding: 0, fontSize: 'var(--t-note)', color: 'var(--teal-700)', fontWeight: 500, cursor: 'pointer' } as const;
const infoNote = { display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 'var(--t-note)', color: 'var(--ink-500)', lineHeight: 1.5 } as const;

/** La clé machine d'un refus du serveur (`name_taken`, `flow_taken`…), ou `null`. */
const refusalKey = (e: unknown): string | null => (e instanceof ApiError ? e.key : null);

/** Un nom de fichier sûr tiré du nom du mapping — le téléchargement sert à corriger la version hors de la console. */
function fileName(name: string, version: number): string {
  const base = name.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase();
  return `${base || 'mapping'}-v${version}.json`;
}

/**
 * Les champs produits, en une liste : ceux de l'en-tête, puis ceux d'une ligne sous le chemin de la liste
 * (« lines.gtin »). La règle vit au serveur (`declaredFields`) — l'écran la recopiait et ratait tout l'EDIFACT.
 */
export function producedFieldList(p: ApiMappingDetail['producedFields']): string[] {
  if (!p) return [];
  return [...p.header, ...(p.lines ? p.lines.fields.map((f) => `${p.lines!.path}.${f}`) : [])];
}

// Zone de dépôt d'un fichier : on charge le fichier de l'intégrateur plutôt que de l'écrire à la main. `read` : son
// texte (le mapping, du JSON), ou ses OCTETS en base64 (un fichier du fournisseur, que seul le serveur sait décoder).
function FileDrop({ title, hint, accept, loaded, read = 'text', onLoad }: { title: string; hint: string; accept: string; loaded: string | null; read?: 'text' | 'bytes'; onLoad: (content: string, name: string) => void }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 12, border: `1.5px dashed ${loaded ? 'var(--teal-500)' : 'var(--ink-300)'}`, borderRadius: 'var(--r-sm)', padding: '12px 14px', cursor: 'pointer', background: loaded ? 'var(--teal-50)' : 'var(--paper)' }}>
      <input type="file" accept={accept} style={{ display: 'none' }}
        onChange={async (e) => {
          const f = e.target.files?.[0];
          if (f) onLoad(read === 'bytes' ? bytesToBase64(new Uint8Array(await f.arrayBuffer())) : await f.text(), f.name);
        }} />
      <span style={{ width: 34, height: 34, borderRadius: 'var(--r-sm)', background: loaded ? 'var(--teal-100)' : 'var(--ink-100)', color: loaded ? 'var(--teal-700)' : 'var(--ink-500)', display: 'grid', placeItems: 'center', flexShrink: 0 }}><Icon name={loaded ? 'check' : 'upload'} size={16} /></span>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-800)' }}>{loaded ? `Chargé : ${loaded}` : title}</span>
        <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginTop: 2, lineHeight: 1.45 }}>{hint}</span>
      </span>
    </label>
  );
}

/**
 * Le choix des règles et leur validation RÉELLE par le moteur — partagé par la création d'un mapping et par une
 * nouvelle version. Rend les règles lues et le verdict ; `null` tant que rien n'est validé.
 */
function useRulesPicker(initialSample?: SampleFile | null) {
  const { showToast } = useApp();
  const [rulesText, setRulesText] = useState('');
  const [rulesName, setRulesName] = useState<string | null>(null);
  const [sample, setSample] = useState(initialSample?.base64 ?? '');
  const [sampleName, setSampleName] = useState<string | null>(initialSample?.name ?? null);
  const [paste, setPaste] = useState(false);
  const [res, setRes] = useState<MappingValidateResult | null>(null);
  const [busy, setBusy] = useState(false);

  const parseRules = (): unknown | undefined => {
    try { return JSON.parse(rulesText); } catch { showToast('Fichier de mapping invalide (JSON) — vérifiez le fichier', 'err'); return undefined; }
  };
  // ⚠️ Le nom du fichier d'exemple part avec lui : un mapping peut lire le magasin dans le nom (`fileNameGln`). Et l'exemple
  //    part AUSSI avec l'écriture : le serveur en refait le rapport et le CONSERVE pour la version — « Dernier rapport ».
  const samplePayload = (): { sampleBase64?: string; sampleName?: string } =>
    sample ? { sampleBase64: sample, ...(sampleName ? { sampleName } : {}) } : {};
  const validate = async () => {
    const rules = parseRules(); if (rules === undefined) return;
    setBusy(true);
    try { setRes(await api.post<MappingValidateResult>('/admin/mappings/validate', { rules, ...samplePayload() })); }
    catch (e) { showToast('Validation impossible : ' + (e as Error).message, 'err'); }
    finally { setBusy(false); }
  };
  // ⚠️ Les deux familles de refus comptent : l'écriture est refusée par le serveur dans les DEUX cas, donc un
  //    bouton encore actif enverrait l'opérateur chercher la panne ailleurs (§23.0).
  const valid = !!res?.valid && (res.shapeErrors?.length ?? 0) === 0 && (res.storeRedirections?.length ?? 0) === 0;

  const fields = (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={sectionSt}>Fichier de mapping</div>
        <FileDrop title="Déposer le fichier de mapping (.json)" hint="Décrit la traduction du flux vers le format interne — livré par l’intégrateur." accept=".json,application/json"
          loaded={rulesName} onLoad={(t, n) => { setRulesText(t); setRulesName(n); setRes(null); }} />
        <button type="button" onClick={() => setPaste((p) => !p)} style={{ ...linkBtn, alignSelf: 'flex-start' }}>{paste ? 'Masquer le JSON collé' : 'Coller le JSON à la place'}</button>
        {paste && <textarea aria-label="Règles du mapping (JSON)" value={rulesText} onChange={(e) => { setRulesText(e.target.value); setRulesName(null); setRes(null); }} placeholder='{ "format": "xml", "rootType": "…", "canonicalType": "…", … }' style={{ ...ta, height: 130 }} />}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={sectionSt}>Fichier exemple <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500, color: 'var(--ink-400)' }}>· optionnel</span></div>
        <FileDrop title="Déposer un fichier du fournisseur" hint="Pour valider : la console tente une vraie traduction et montre ce que le back-office recevrait." accept="*" read="bytes"
          loaded={sampleName ?? (sample ? 'le fichier reçu' : null)} onLoad={(bytes, n) => { setSample(bytes); setSampleName(n); setRes(null); }} />
      </div>
    </>
  );
  return { fields, res, valid, busy, setBusy, validate, parseRules, samplePayload, hasRules: !!rulesText.trim() };
}

// Le verdict de validation RÉEL : forme, routage, et ce que le back-office recevrait de l'exemple.
function ValidationReport({ res }: { res: MappingValidateResult }) {
  if (res.shapeErrors && res.shapeErrors.length > 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--rose-fg)' }}>Forme du mapping invalide</div>
        {res.shapeErrors.map((e, i) => <div key={i} style={{ display: 'flex', gap: 8, fontSize: 'var(--t-dense)', color: 'var(--ink-700)' }}><Dot kind="err" />{e}</div>)}
      </div>
    );
  }
  if (res.storeRedirections && res.storeRedirections.length > 0) {
    /* ⚠️ Titre PROPRE, jamais « forme invalide » : la forme est correcte, c'est le ROUTAGE qui est faux — annoncer
       une erreur de forme enverrait corriger un champ qui n'a rien. */
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--rose-fg)' }}>Redirection de magasin — refusé</div>
        {res.storeRedirections.map((e, i) => <div key={i} style={{ display: 'flex', gap: 8, fontSize: 'var(--t-dense)', color: 'var(--ink-700)' }}><Dot kind="err" />{e}</div>)}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ok-fg)', display: 'flex', alignItems: 'center', gap: 6 }}><Icon name="check" size={14} sw={3} />Forme valide — « {res.messageType} » → {res.canonicalType}</div>
      {res.report && <ReportBody report={res.report} narrow />}
    </div>
  );
}

const plural = (n: number, one: string, many: string) => `${n} ${n > 1 ? many : one}`;

/**
 * Le corps d'un rapport du moteur — compteurs, diagnostics, et ce que le back-office recevrait. LE MÊME pour la
 * validation avant import et pour le dernier rapport conservé d'une version : deux rendus finiraient par compter
 * autrement le même fichier.
 * ⚠️ L'APERÇU DIT SES COUPES : trois lignes montrées comme le document entier feraient croire que le fournisseur n'en
 *    envoie que trois. ⚠️ « Valeurs traduites » et non « champs » : un catalogue de mille lignes en pose des milliers.
 */
function ReportBody({ report, narrow }: { report: MappingReport; narrow?: boolean }) {
  if (!report.recognized) {
    return <div style={{ padding: '8px 10px', borderRadius: 'var(--r-xs)', background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: 'var(--t-dense)', lineHeight: 1.5 }}>Ce fichier n’est pas reconnu par ce mapping : {report.reason}</div>;
  }
  const counters: Array<[number, string, 'ok' | 'warn' | 'err']> = [
    [report.valuesTranslated ?? 0, (report.valuesTranslated ?? 0) > 1 ? 'valeurs traduites' : 'valeur traduite', 'ok'],
    [report.blocking, report.blocking > 1 ? 'bloquants' : 'bloquant', report.blocking ? 'err' : 'ok'],
    [report.warnings, report.warnings > 1 ? 'avertissements' : 'avertissement', report.warnings ? 'warn' : 'ok'],
  ];
  const hidden = report.diagnosticsTotal - report.diagnostics.length;
  const col = (tone: 'ok' | 'warn' | 'err') => (tone === 'ok' ? 'var(--ok-fg)' : tone === 'err' ? 'var(--rose-fg)' : 'var(--amber-fg)');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${counters.length}, minmax(0,1fr))`, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', overflow: 'hidden' }}>
        {counters.map(([n, label, tone], i) => (
          <div key={label} style={{ padding: '12px 14px', minWidth: 0, borderRight: i < counters.length - 1 ? '1px solid var(--ink-100)' : undefined }}>
            <div style={{ fontSize: 'var(--t-feature)', fontWeight: 700, letterSpacing: '-0.035em', fontVariantNumeric: 'tabular-nums', color: col(tone) }}>{n}</div>
            <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', lineHeight: 1.35 }}>{label}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: narrow ? 'minmax(0,1fr)' : 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7, minWidth: 0 }}>
          <div style={sectionSt}>Diagnostics</div>
          {report.diagnostics.length === 0 && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '9px 11px', borderRadius: 'var(--r-sm)', background: 'var(--ok-bg)' }}>
              <span style={{ display: 'flex', marginTop: 5 }}><Dot kind="ok" /></span>
              <div style={{ fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--ink-900)' }}>Aucun diagnostic — le moteur n’a rien eu à signaler sur ce fichier.</div>
            </div>
          )}
          {report.diagnostics.map((d, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '9px 11px', borderRadius: 'var(--r-sm)', background: d.kind === 'err' ? 'var(--rose-bg)' : 'var(--amber-bg)' }}>
              <span style={{ display: 'flex', marginTop: 5 }}><Dot kind={d.kind} /></span>
              <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--ink-900)' }}>{d.title}</div><div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-600)', lineHeight: 1.5, marginTop: 1 }}>{d.detail}</div></div>
            </div>
          ))}
          {hidden > 0 && <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>… et {plural(hidden, 'autre diagnostic', 'autres diagnostics')}, non conservés.</div>}
          <div style={{ ...infoNote, marginTop: 3 }}><Icon name="info" size={14} /><span>Une correction se fait dans le fichier du mapping, chez l’intégrateur, puis s’importe en nouvelle version. La console ne modifie jamais un mapping.</span></div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7, minWidth: 0 }}>
          <div style={sectionSt}>Ce que le back-office recevrait</div>
          <pre style={{ margin: 0, padding: '12px 13px', background: 'var(--ink-50)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', fontSize: 'var(--t-meta)', lineHeight: 1.7, fontFamily: 'var(--font-mono)', color: 'var(--ink-800)', overflow: 'auto', maxHeight: 300 }}>{JSON.stringify(report.canonical, null, 2)}</pre>
          {report.cut.length > 0 && (
            <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', lineHeight: 1.5 }}>
              Aperçu : {report.cut.filter((c) => !c.path.includes('[')).map((c) => `« ${c.path} », ${c.kept} sur ${c.total}`).join(' · ') || 'listes imbriquées coupées'}.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Le résultat d'une validation dans son cadre, et l'aide de pied qui dit pourquoi le bouton d'écriture attend. */
function ResultBox({ res, extra }: { res: MappingValidateResult; extra?: string | null }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 9, padding: 14, borderRadius: 'var(--r-sm)', border: '1px solid var(--ink-200)', background: 'var(--paper)' }}>
      <ValidationReport res={res} />
      {extra && <div role="alert" style={{ padding: '8px 11px', borderRadius: 'var(--r-sm)', background: 'var(--rose-bg)', color: 'var(--rose-fg)', fontSize: 'var(--t-note)', lineHeight: 1.5 }}>{extra}</div>}
    </div>
  );
}

/** L'aide du pied : ce qui manque pour écrire, dans l'ordre où on le fait. */
function footHelp(steps: Array<[boolean, string]>, done: string): { text: string; ok: boolean } {
  const missing = steps.find(([ok]) => !ok);
  return missing ? { text: missing[1], ok: false } : { text: done, ok: true };
}

/** « Créer » s'active quand le type est choisi, le fichier validé ET conforme à ce type (et au flux exigé), et le nom donné. */
export function canCreateMapping(s: { type: string; valid: boolean; producedType: string | null | undefined; name: string; flowMatches?: boolean }): boolean {
  return !!s.type && s.valid && s.producedType === s.type && s.flowMatches !== false && !!s.name.trim();
}

/**
 * Créer un mapping. Le TYPE se choisit d'abord (la validation vérifie que le fichier le produit), puis le nom et les
 * règles ; « Créer » ne s'active qu'après une validation réussie.
 * ⚠️ AUCUN FOURNISSEUR À CHOISIR ICI : un mapping se rattache depuis la fiche du fournisseur. Lancée depuis « Rattacher »,
 *    la fenêtre reçoit ce fournisseur et le dit — le serveur rattache dans la même écriture, tout ou rien.
 */
export function NewMappingModal({ preset, onClose, onCreated }: {
  /** `flow` + `sample` : lancée depuis un fichier NON RECONNU — le mapping devra reconnaître son flux, le fichier sert d'exemple. */
  preset: { type?: string | null; supplier?: { id: string; name: string } | null; pendingSupplierName?: string | null; flow?: ReadFlow | null; sample?: SampleFile | null };
  onClose: () => void;
  onCreated: (created: { id: string; name: string }) => void | Promise<void>;
}) {
  const fieldId = useId();
  const { showToast, guard } = useApp();
  const picker = useRulesPicker(preset.sample);
  const typeLocked = !!preset.type;
  const [type, setType] = useState<string>(preset.type ?? '');
  const [name, setName] = useState('');
  const [nameTaken, setNameTaken] = useState(false);

  // Un fichier qui produit un autre type que celui choisi est refusé ici : on le rattacherait sur le mauvais type.
  const otherType = picker.valid && !!type && picker.res?.canonicalType !== type ? picker.res?.canonicalType ?? null : null;
  // Depuis un fichier non reconnu, un mapping qui reconnaît un AUTRE flux ne le traduirait toujours pas.
  const flow = preset.flow ?? null;
  const flowMatches = !flow || !picker.valid || (picker.res?.format === flow.format && picker.res?.messageType === flow.discriminant);
  const ready = picker.valid && !otherType && flowMatches;
  const creatable = canCreateMapping({ type, valid: picker.valid, producedType: picker.res?.canonicalType, name, flowMatches });
  const doCreate = async () => {
    if (!guard()) return;
    const rules = picker.parseRules(); if (rules === undefined) return;
    picker.setBusy(true);
    try {
      const { id } = await api.post<{ id: string; version: number }>('/admin/mappings', { name, rules, supplierIds: preset.supplier ? [preset.supplier.id] : [], ...picker.samplePayload() });
      showToast(`Mapping « ${name.trim()} » créé${preset.supplier ? ` et rattaché à ${preset.supplier.name}` : ''}`, 'ok');
      await onCreated({ id, name: name.trim() });
    } catch (e) {
      if (refusalKey(e) === 'name_taken') setNameTaken(true);
      showToast('Refusé : ' + (e as Error).message, 'err');
      picker.setBusy(false);
    }
  };
  const help = footHelp([
    [!!type, 'Choisissez le type, déposez le fichier puis validez : « Créer » s’active après une validation réussie.'],
    [picker.hasRules, 'Déposez le fichier de mapping puis validez-le : « Créer » s’active après une validation réussie.'],
    [ready, 'Validez le mapping : « Créer » s’active après une validation réussie.'],
    [!!name.trim(), 'Validé — donnez-lui un nom pour le créer.'],
  ], 'Validé — prêt à être créé.');
  const preamble = [
    flow ? `Flux lu à la réception : ${readFlowLabel(flow.format, flow.discriminant)} — le mapping devra le reconnaître ; le fichier reçu sert d’exemple.` : null,
    preset.supplier ? `Il sera rattaché à ${preset.supplier.name} dès sa création.`
      : preset.pendingSupplierName !== undefined ? `Il sera rattaché à ${preset.pendingSupplierName || 'ce fournisseur'} à son enregistrement.` : null,
  ].filter(Boolean).join(' ') || null;

  return (
    <Modal icon="plus" maxWidth={720} title="Nouveau mapping" sub="Validé sur un exemple avant d’être mis en service." onClose={onClose}
      footer={<>
        <Btn variant="ghost" mutating={false} onClick={onClose}>Annuler</Btn>
        <span style={{ flex: 1, minWidth: 200, fontSize: 'var(--t-meta)', lineHeight: 1.45, color: help.ok ? 'var(--ok-fg)' : 'var(--ink-500)' }}>{help.text}</span>
        <Btn variant="ghost" mutating={false} icon="sparkles" disabled={picker.busy || !picker.hasRules}
          onClick={() => { if (!type) { showToast('Choisissez d’abord le type de fichier', 'warn'); return; } void picker.validate(); }}>{picker.busy ? 'Validation…' : 'Valider'}</Btn>
        <Btn icon="check" disabled={picker.busy || !creatable} onClick={() => void doCreate()}>Créer</Btn>
      </>}>
      <div style={{ display: 'grid', gap: 16 }}>
        <div>
          {typeLocked ? (
            <>
              <div style={sectionSt}>Type de fichier</div>
              <div style={{ marginTop: 5, display: 'flex', alignItems: 'center', gap: 10, height: 40, padding: '0 12px', borderRadius: 'var(--r-ctrl)', border: '1px solid var(--ink-200)', background: 'var(--ink-50)' }}>
                <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-900)' }}>{msgTypeLabel(type)}</span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>{type}</span>
              </div>
            </>
          ) : (
            <>
              <Picker id={`${fieldId}-type`} variant="field" label="Type de fichier" allLabel="Choisir un type"
                options={INBOUND_MSG_TYPES.map((t) => ({ value: t.key, label: t.label, hint: t.key }))}
                value={type} onChange={(v) => { setType(v); }} />
              <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', marginTop: 5, lineHeight: 1.45 }}>Ce que le fichier produit pour le back-office. La validation vérifie que le mapping produit bien ce type.</div>
            </>
          )}
        </div>
        <div>
          <label htmlFor={`${fieldId}-name`} style={sectionSt}>Nom</label>
          <input id={`${fieldId}-name`} value={name} onChange={(e) => { setName(e.target.value); setNameTaken(false); }} placeholder="Bestseller — factures XML" style={{ ...inp, marginTop: 5 }} />
          {nameTaken && <div role="alert" style={{ fontSize: 'var(--t-meta)', color: 'var(--rose-fg)', marginTop: 5 }}>Un autre mapping porte déjà ce nom — c’est le nom qui le désigne à l’écran.</div>}
        </div>
        {preamble && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '10px 12px', borderRadius: 'var(--r-sm)', background: 'var(--blue-bg)', color: 'var(--blue-fg)', fontSize: 'var(--t-dense)', lineHeight: 1.5 }}>
            <Icon name="info" size={15} /><span>{preamble}</span>
          </div>
        )}
        {picker.fields}
        {picker.res && <ResultBox res={picker.res} extra={otherType ? `Ce fichier produit ${otherType}, pas ${type} : choisissez le bon type ou un autre fichier.`
          : !flowMatches && flow ? `Ce mapping reconnaît ${picker.res.format ?? '?'} « ${picker.res.messageType ?? '?'} » ; le fichier reçu est lu ${readFlowLabel(flow.format, flow.discriminant)} : il ne le traduirait toujours pas.` : null} />}
      </div>
    </Modal>
  );
}

// Nouvelle version d'un mapping : elle s'applique AUSSITÔT à tous ses fournisseurs — l'écran les nomme avant l'import.
function NewVersionModal({ mapping, onClose, onDone }: { mapping: ApiMappingDetail; onClose: () => void; onDone: () => void | Promise<void> }) {
  const { showToast, guard } = useApp();
  const picker = useRulesPicker();
  // Une version qui reconnaît un autre flux ou produit autre chose est refusée par le serveur : l'écran le dit
  // dès la validation, plutôt que d'offrir un bouton qui échouera.
  const otherFlow = picker.valid && (picker.res?.messageType !== mapping.discriminant || picker.res?.canonicalType !== mapping.canonicalType);
  const doImport = async () => {
    if (!guard()) return;
    const rules = picker.parseRules(); if (rules === undefined) return;
    picker.setBusy(true);
    try {
      const { version } = await api.post<{ version: number }>(`/admin/mappings/${mapping.id}/versions`, { rules, ...picker.samplePayload() });
      showToast(`Version ${version} en service${mapping.suppliers.length ? ` pour ${mapping.suppliers.map((s) => s.name).join(', ')}` : ''}`, 'ok');
      await onDone();
    } catch (e) { showToast('Refusé : ' + (e as Error).message, 'err'); picker.setBusy(false); }
  };
  const help = footHelp([
    [picker.hasRules, 'Déposez le fichier de mapping puis validez-le : « Mettre en service » s’active après une validation réussie.'],
    [picker.valid && !otherFlow, 'Validez le mapping : « Mettre en service » s’active après une validation réussie.'],
  ], 'Validé — prêt à être mis en service.');

  return (
    <Modal icon="upload" maxWidth={720} title="Nouvelle version" sub={mapping.name} onClose={onClose}
      footer={<>
        <Btn variant="ghost" mutating={false} onClick={onClose}>Annuler</Btn>
        <span style={{ flex: 1, minWidth: 200, fontSize: 'var(--t-meta)', lineHeight: 1.45, color: help.ok ? 'var(--ok-fg)' : 'var(--ink-500)' }}>{help.text}</span>
        <Btn variant="ghost" mutating={false} icon="sparkles" disabled={picker.busy || !picker.hasRules} onClick={() => void picker.validate()}>{picker.busy ? 'Validation…' : 'Valider'}</Btn>
        <Btn icon="check" disabled={picker.busy || !picker.valid || !!otherFlow} onClick={() => void doImport()}>Mettre en service</Btn>
      </>}>
      <div style={{ display: 'grid', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '10px 12px', borderRadius: 'var(--r-sm)', background: mapping.suppliers.length ? 'var(--amber-bg)' : 'var(--ink-50)', color: mapping.suppliers.length ? 'var(--amber-fg)' : 'var(--ink-600)', fontSize: 'var(--t-dense)', lineHeight: 1.5 }}>
          <Icon name="info" size={15} />
          <span>{mapping.suppliers.length
            ? <>Cette version traduira aussitôt pour <b style={{ fontWeight: 600 }}>{mapping.suppliers.map((s) => s.name).join(', ')}</b>. La version {mapping.currentVersion} restera disponible pour revenir en arrière.</>
            : <>Ce mapping n’est rattaché à aucun fournisseur : la version ne traduira rien tant qu’il ne l’est pas.</>}</span>
        </div>
        {picker.fields}
        {picker.res && <ResultBox res={picker.res} extra={otherFlow ? `Ce fichier reconnaît « ${picker.res.messageType} » et produit ${picker.res.canonicalType} ; « ${mapping.name} » reconnaît « ${mapping.discriminant} » et produit ${mapping.canonicalType}. C’est un autre mapping : créez-le avec « Nouveau mapping ».` : null} />}
      </div>
    </Modal>
  );
}

/**
 * Rattacher un mapping existant à un fournisseur pour un type, ou en créer un. Les candidats sont lus au serveur à
 * l'ouverture : ceux qui produisent ce type et ne sont pas déjà rattachés. Un candidat dont le flux arrive déjà par un
 * autre mapping est refusé ici en le nommant.
 * `onAttach` écrit (fournisseur enregistré) ou retient le choix (fournisseur en cours de création) ; la fenêtre se
 * ferme quand il a réussi.
 * ⚠️ DEPUIS UN FICHIER NON RECONNU (`flow`), elle ne part pas d'un type — un fichier non reconnu n'en a pas — mais du
 *    flux lu à la réception : seuls les mappings qui le reconnaissent peuvent le traduire, quel que soit leur type.
 */
export function AttachModal({ supplierName, type, flow, attached, onClose, onAttach, onCreate }: {
  supplierName: string; attached: readonly AttachedMapping[];
  onClose: () => void; onAttach: (m: ApiMapping) => Promise<void>; onCreate: () => void;
} & ({ type: string; flow?: undefined } | { type?: undefined; flow: ReadFlow })) {
  const { guard } = useApp();
  const [mappings, setMappings] = useState<ApiMapping[] | null>(null);
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let live = true;
    api.get<ApiMapping[]>('/admin/mappings').then((r) => { if (live) setMappings(r); }, (e: Error) => { if (live) setLoadError(e.message); });
    return () => { live = false; };
  }, []);
  const attachedIds = attached.map((m) => m.id);
  const candidates = !mappings ? [] : flow ? flowCandidates(mappings, flow, attachedIds) : attachCandidates(mappings, type ?? '', attachedIds);
  const attach = async (m: ApiMapping) => {
    if (!guard()) return;
    setBusy(true);
    try { await onAttach(m); } finally { setBusy(false); }
  };

  return (
    <Modal icon="layers" maxWidth={640} title={flow ? 'Rattacher un mapping pour ce flux' : `Rattacher un mapping ${type}`}
      sub={flow ? `${supplierName} · ${readFlowLabel(flow.format, flow.discriminant)}` : `${supplierName} · ${msgTypeLabel(type ?? '')}`} onClose={onClose}
      footer={<>
        <span style={{ flex: 1 }} />
        <Btn variant="ghost" mutating={false} onClick={onClose}>Annuler</Btn>
        <Btn variant="ghost" icon="plus" onClick={() => { if (guard()) onCreate(); }}>Créer un nouveau mapping</Btn>
      </>}>
      <div style={{ display: 'grid', gap: 16 }}>
        <p style={{ margin: 0, fontSize: 'var(--t-dense)', color: 'var(--ink-600)', lineHeight: 1.55 }}>
          {flow
            ? <>Seuls les mappings qui reconnaissent ce flux peuvent traduire ce fichier. Une fois rattaché, rejouez le message : il sera traduit.</>
            : <>Un mapping ne reconnaît que son flux, indiqué sous son nom : rattachez celui qui correspond aux fichiers de {supplierName}.</>}
        </p>
        {!mappings && !loadError && <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}><Spinner size={20} /></div>}
        {loadError && <div role="alert" style={{ fontSize: 'var(--t-body)', color: 'var(--rose-fg)' }}>Mappings indisponibles : {loadError}</div>}
        {mappings && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {candidates.length === 0 && (
              <div style={{ padding: 14, borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)', fontSize: 'var(--t-dense)', color: 'var(--ink-600)', lineHeight: 1.5 }}>
                {flow ? 'Aucun mapping existant ne reconnaît ce flux — créez-en un, à partir du fichier reçu.' : `Aucun mapping existant ne produit ${type} — créez-en un.`}
              </div>
            )}
            {candidates.map((m) => {
              const other = attachConflict(m, attached);
              return (
                <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 12px 12px 14px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 230 }}>
                    <div style={{ fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-900)' }}>{m.name}</div>
                    <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>{flowToLabel(m)} · v{m.currentVersion}{flow ? ` · ${msgTypeLabel(m.canonicalType)}` : ''}</div>
                    <div style={{ fontSize: 'var(--t-meta)', marginTop: 3, color: other ? 'var(--rose-fg)' : 'var(--ink-500)' }}>
                      {other ? `${supplierName} reçoit déjà ce flux par « ${other.name} »` : m.suppliers.length ? `déjà utilisé par ${m.suppliers.map((s) => s.name).join(', ')}` : 'utilisé par aucun fournisseur'}
                    </div>
                  </div>
                  <Btn size="sm" icon="check" disabled={busy || !!other} onClick={() => void attach(m)}>
                    <span aria-label={`Rattacher ${m.name} à ${supplierName}`}>Rattacher</span>
                  </Btn>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}

/**
 * La fiche d'un mapping : son nom, ses fournisseurs (Voir la fiche, Détacher), ses versions (remettre en service,
 * télécharger), les champs qu'il produit, une nouvelle version, et la suppression quand plus personne ne l'emploie.
 * `pausedIds` : les fournisseurs en pause, dits comme tels. `onOpenSupplier` : ouvrir la fiche d'un fournisseur.
 * ⚠️ Sans `onOpenSupplier`, « Voir la fiche » n'est pas offert : un bouton qui ne ferait que fermer la fenêtre ferait
 *    semblant (§23.0) — c'est le cas dans l'assistant, qui modifie déjà un fournisseur.
 */
export function MappingSheet({ id, pausedIds, onClose, onChanged, onOpenSupplier }: {
  id: string; pausedIds?: ReadonlySet<string>;
  onClose: () => void; onChanged: () => Promise<void> | void; onOpenSupplier?: (supplierId: string) => void;
}) {
  const fieldId = useId();
  const panelId = `${fieldId}-panel`;
  const { showToast, guard } = useApp();
  const [d, setD] = useState<ApiMappingDetail | null>(null);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState<'sheet' | 'report' | 'fields'>('sheet');
  const [checking, setChecking] = useState(false);
  const [name, setName] = useState('');
  const [nameTaken, setNameTaken] = useState(false);
  const [busy, setBusy] = useState(false);
  const [versionOpen, setVersionOpen] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let live = true;
    api.get<ApiMappingDetail>(`/admin/mappings/${id}`).then(
      (r) => { if (live) { setD(r); setName(r.name); setErr(''); } },
      (e: Error) => { if (live) setErr(e.message); },
    );
    return () => { live = false; };
  }, [id, tick]);

  // Une écriture, puis la relecture du mapping ET de l'écran : ce qui s'affiche vient du serveur, jamais d'une supposition.
  const act = async (fn: () => Promise<unknown>, success: string, tone: 'ok' | 'warn' = 'ok'): Promise<boolean> => {
    if (!guard()) return false;
    setBusy(true);
    try { await fn(); showToast(success, tone); setTick((t) => t + 1); await onChanged(); return true; }
    catch (e) {
      if (refusalKey(e) === 'name_taken') setNameTaken(true);
      showToast('Refusé : ' + (e as Error).message, 'err');
      return false;
    } finally { setBusy(false); }
  };
  const download = async (version: number) => {
    try {
      const { rules } = await api.get<{ version: number; rules: unknown }>(`/admin/mappings/${id}/versions/${version}`);
      const url = URL.createObjectURL(new Blob([JSON.stringify(rules, null, 2)], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName(d?.name ?? 'mapping', version);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { showToast('Téléchargement impossible : ' + (e as Error).message, 'err'); }
  };
  const detach = async (s: { id: string; name: string }) => {
    if (!d || !guard()) return;
    const ok = await confirm({
      title: `Détacher ${s.name} ?`,
      message: `Ses fichiers ${flowLabel(d)} ne seront plus traduits : ils arriveront « non reconnus » et attendront un mapping.`,
      confirmLabel: 'Détacher',
    });
    if (ok) await act(() => api.del(`/admin/mappings/${id}/suppliers/${s.id}`), `${s.name} détaché — ses fichiers ${d.canonicalType} ne seront plus traduits par ce mapping`, 'warn');
  };
  /** Revérifier : la version en service rejouée sur le dernier fichier de son flux — le serveur conserve le rapport. */
  const recheck = async () => {
    if (!d || !guard()) return;
    setChecking(true);
    try {
      const r = await api.post<{ version: number; fileName: string | null; report: MappingReport }>(`/admin/mappings/${id}/recheck`);
      const on = r.fileName ? ` sur « ${r.fileName} »` : '';
      if (!r.report.recognized) showToast(`Revérifié${on} : ce fichier n’est pas reconnu par la version ${r.version}`, 'warn');
      else if (r.report.blocking > 0) showToast(`Revérifié${on} : ${plural(r.report.blocking, 'diagnostic bloquant', 'diagnostics bloquants')}`, 'warn');
      else showToast(`Revérifié${on} : aucun blocage`, 'ok');
      setTick((t) => t + 1);
    } catch (e) { showToast('Revérification impossible : ' + (e as Error).message, 'err'); }
    finally { setChecking(false); }
  };
  const remove = async () => {
    if (!d || !guard()) return;
    const n = d.versions.length;
    const ok = await confirm({
      title: `Supprimer « ${d.name} » ?`,
      message: `${n > 1 ? `Ses ${n} versions seront supprimées` : 'Sa version sera supprimée'} définitivement. Il ne traduit rien : aucun fournisseur n’est touché.`,
      confirmLabel: 'Supprimer',
    });
    if (ok && await act(() => api.del(`/admin/mappings/${id}`), `Mapping « ${d.name} » supprimé`)) onClose();
  };

  const fields = d ? producedFieldList(d.producedFields) : [];
  const versions = d ? [...d.versions].sort((a, b) => b.version - a.version) : [];
  const deletable = !!d && d.suppliers.length === 0;

  return (
    <Modal maxWidth={780} title={d?.name ?? 'Mapping'} onClose={onClose}
      tile={d ? <TypeTile type={d.canonicalType} size={38} /> : undefined} accent={d ? typeTint(d.canonicalType).solid : undefined} icon={d ? undefined : 'layers'}
      sub={d ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)' }}>{flowToLabel(d)} · v{d.currentVersion} en service {inServiceLabel({ currentSince: d.currentSince, versionCount: d.versions.length })}</span> : undefined}
      tabs={d ? (
        <Tabs items={[{ id: 'sheet', label: 'Fiche' }, { id: 'report', label: 'Dernier rapport' }, { id: 'fields', label: `Champs produits · ${fields.length}` }]}
          value={tab} onChange={setTab} panelId={panelId} label="Sections du mapping" />
      ) : undefined}
      footer={d ? <>
        <Btn variant="danger" icon="trash" disabled={busy || !deletable} onClick={() => void remove()}>Supprimer</Btn>
        <span style={{ flex: 1 }} />
        <Btn variant="ghost" mutating={false} onClick={onClose}>Fermer</Btn>
        <Btn icon="upload" disabled={busy} onClick={() => { if (guard()) setVersionOpen(true); }}>Nouvelle version</Btn>
      </> : <Btn mutating={false} onClick={onClose}>Fermer</Btn>}>
      {!d && !err && <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><Spinner size={22} /></div>}
      {err && <div role="alert" style={{ fontSize: 'var(--t-body)', color: 'var(--rose-fg)' }}>Mapping indisponible : {err}</div>}
      {d && (
      <div id={panelId} role="tabpanel" aria-labelledby={`${panelId}-tab-${tab}`}>
      {tab === 'sheet' && (
        <div style={{ display: 'grid', gap: 18 }}>
          <div>
            <label htmlFor={`${fieldId}-name`} style={sectionSt}>Nom</label>
            <div style={{ display: 'flex', gap: 8, marginTop: 5 }}>
              <input id={`${fieldId}-name`} value={name} onChange={(e) => { setName(e.target.value); setNameTaken(false); }} style={inp} />
              <Btn variant="ghost" disabled={busy || !name.trim() || name.trim() === d.name} onClick={() => void act(() => api.patch(`/admin/mappings/${id}`, { name }), 'Mapping renommé')}>Renommer</Btn>
            </div>
            {nameTaken && <div role="alert" style={{ fontSize: 'var(--t-meta)', color: 'var(--rose-fg)', marginTop: 5 }}>Un autre mapping porte déjà ce nom.</div>}
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}><span style={sectionSt}>Fournisseurs rattachés</span><span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>{d.suppliers.length}</span></div>
            {d.suppliers.length === 0 && (
              <div style={{ marginTop: 8, padding: '10px 12px', borderRadius: 'var(--r-sm)', background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: 'var(--t-dense)', lineHeight: 1.5 }}>Aucun — ce mapping ne traduit rien tant qu’il n’est pas rattaché.</div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
              {d.suppliers.map((s) => {
                const paused = pausedIds?.has(s.id) ?? false;
                return (
                  <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 8px 7px 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)' }}>
                    <Dot kind={paused ? 'off' : 'ok'} />
                    <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-800)' }}>{s.name}</span>
                    {paused && <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>en pause</span>}
                    {onOpenSupplier && (
                      <button type="button" onClick={() => onOpenSupplier(s.id)} style={{ ...linkBtn, padding: '0 6px', whiteSpace: 'nowrap' }}>
                        <span aria-label={`Voir la fiche de ${s.name}`}>Voir la fiche</span>
                      </button>
                    )}
                    <Btn size="sm" variant="ghost" disabled={busy} onClick={() => void detach(s)}>
                      <span aria-label={`Détacher ${s.name}`}>Détacher</span>
                    </Btn>
                  </div>
                );
              })}
            </div>
            <div style={{ ...infoNote, marginTop: 9 }}><Icon name="info" size={14} /><span>Un mapping se rattache depuis la fiche d’un fournisseur, onglet Mappings.</span></div>
            {d.suppliers.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9, marginTop: 10, padding: '10px 12px', borderRadius: 'var(--r-sm)', background: 'var(--blue-bg)', color: 'var(--blue-fg)', fontSize: 'var(--t-note)', lineHeight: 1.5 }}>
                <Icon name="info" size={14} /><span>Pour le supprimer, détachez-le d’abord de ses fournisseurs : leur traduction s’arrêterait sans un mot.</span>
              </div>
            )}
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}><span style={sectionSt}>Versions</span><span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>la version en service traduit pour tous ses fournisseurs</span></div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
              {versions.map((v) => {
                const live = v.version === d.currentVersion;
                return (
                  <div key={v.version} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 8px 7px 12px', border: `1px solid ${live ? 'var(--teal-300)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-sm)', flexWrap: 'wrap', background: live ? 'var(--teal-50)' : 'var(--paper)' }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 'var(--t-note)', color: 'var(--ink-800)', width: 30, flexShrink: 0 }}>v{v.version}</span>
                    <span style={{ flex: 1, minWidth: 130, fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>{dateTime(v.createdAt)}</span>
                    {live
                      ? <Badge kind="ok">en service</Badge>
                      : (
                        <Btn size="sm" variant="ghost" disabled={busy} onClick={() => void act(() => api.put(`/admin/mappings/${id}/current`, { version: v.version }), `Version ${v.version} remise en service${d.suppliers.length ? ` pour ${d.suppliers.map((s) => s.name).join(', ')}` : ''}`)}>
                          <span aria-label={`Remettre en service la version ${v.version}`}>Remettre en service</span>
                        </Btn>
                      )}
                    <Btn size="sm" variant="ghost" mutating={false} icon="download" onClick={() => void download(v.version)}>
                      <span aria-label={`Télécharger la version ${v.version}`}>JSON</span>
                    </Btn>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {tab === 'report' && <StoredReport mapping={d} checking={checking} busy={busy} onRecheck={() => void recheck()} />}
      {tab === 'fields' && (
        <div style={{ display: 'grid', gap: 14 }}>
          <p style={{ margin: 0, fontSize: 'var(--t-dense)', color: 'var(--ink-600)', lineHeight: 1.55 }}>Les champs du format interne que la version en service alimente. Un champ absent de cette liste arrive vide au back-office.</p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {fields.length === 0 && <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-400)' }}>aucun champ produit</span>}
            {fields.map((f) => <span key={f} style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', color: 'var(--ink-700)', background: 'var(--ink-100)', padding: '3px 8px', borderRadius: 'var(--r-xs)' }}>{f}</span>)}
          </div>
        </div>
      )}
      </div>
      )}
      {versionOpen && d && <NewVersionModal mapping={d} onClose={() => setVersionOpen(false)} onDone={async () => { setVersionOpen(false); setTick((t) => t + 1); await onChanged(); }} />}
    </Modal>
  );
}

/** D'où vient le rapport conservé, en une ligne : le fichier, quand, et comment il a été obtenu. */
function reportOrigin(r: StoredMappingReport): string {
  const file = r.fileName ? `« ${r.fileName} »` : 'un fichier sans nom';
  return r.source === 'recheck' ? `${file} · revérifié le ${dateTime(r.checkedAt)}` : `${file} · exemple de l’import, le ${dateTime(r.checkedAt)}`;
}

/**
 * L'onglet « Dernier rapport » : le rapport CONSERVÉ de la version en service, et « Revérifier » sur le dernier fichier
 * de son flux reçu de ses fournisseurs.
 * ⚠️ Une version mise en service sans fichier exemple n'a pas de rapport, et l'écran le DIT — il ne montre jamais celui
 *    d'une autre version. ⚠️ « Revérifier » sans fichier à relire n'est pas offert comme un geste : il est grisé, et la
 *    raison est écrite à côté (§23.0).
 */
export function StoredReport({ mapping, checking, busy, onRecheck }: { mapping: ApiMappingDetail; checking: boolean; busy: boolean; onRecheck: () => void }) {
  const r = mapping.report;
  const tone = !r ? 'off' : !r.report.recognized || r.report.blocking > 0 ? 'warn' : 'ok';
  const file = mapping.recheckFile;
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ width: 34, height: 34, borderRadius: 'var(--r-ctrl)', display: 'grid', placeItems: 'center', flexShrink: 0, background: tone === 'ok' ? 'var(--ok-bg)' : tone === 'warn' ? 'var(--amber-bg)' : 'var(--ink-100)', color: tone === 'ok' ? 'var(--ok-fg)' : tone === 'warn' ? 'var(--amber-fg)' : 'var(--ink-500)' }}>
          <Icon name={tone === 'ok' ? 'check' : tone === 'warn' ? 'alert' : 'info'} size={17} sw={2.2} />
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: 'var(--t-lead)', fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--ink-900)' }}>Dernier rapport · v{mapping.currentVersion}</div>
          <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', fontFamily: 'var(--font-mono)' }}>{r ? reportOrigin(r) : 'aucun rapport conservé pour cette version'}</div>
        </div>
        <Btn size="sm" variant="ghost" icon="refresh" disabled={busy || checking || !file} onClick={onRecheck}>{checking ? 'Revérification…' : 'Revérifier'}</Btn>
      </div>
      <div style={{ ...infoNote }}>
        <Icon name="info" size={14} />
        <span>{file
          ? `« Revérifier » rejoue la version ${mapping.currentVersion} sur le dernier fichier de ce flux reçu de ses fournisseurs : « ${file.fileName ?? 'sans nom'} », reçu le ${dateTime(file.receivedAt)}. Le message garde son statut : rien ne part au back-office.`
          : 'Aucun fichier de ce flux n’a encore été reçu de ses fournisseurs (ou leurs bruts ont été purgés) : la revérification attend le premier.'}</span>
      </div>
      {r ? <ReportBody report={r.report} />
        : <div style={{ padding: '12px 14px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)', fontSize: 'var(--t-dense)', color: 'var(--ink-600)', lineHeight: 1.5 }}>La version {mapping.currentVersion} a été mise en service sans fichier exemple : aucun rapport n’a été conservé. Revérifiez-la sur le dernier fichier reçu.</div>}
    </div>
  );
}
